<template>
  <!--
    Names what the check searched and narrows what is shown. Filtering is local to the
    results already returned — nothing re-runs, and nothing is re-fetched.
  -->
  <div
    class="mt-5 rounded border border-gray-200 bg-gray-50 px-3.5 py-3"
    data-cy="conflict-field-filter"
  >
    <div class="mb-2 flex flex-wrap items-center gap-3">
      <span class="text-xs font-medium uppercase tracking-wider text-gray-500">
        Searched fields
      </span>
      <span class="text-xs text-gray-500" data-cy="conflict-filter-status">
        {{ statusText }}
      </span>
      <!-- Always in the layout, invisible until it does something, so hiding a field
           never nudges the results above it. -->
      <LlSecondaryButton
        variant="text"
        size="small"
        class="ml-auto"
        :class="{ invisible: !anyHidden }"
        :tabindex="anyHidden ? 0 : -1"
        data-cy="conflict-show-all-fields"
        @click="emit('showAll')"
      >
        Show all fields
      </LlSecondaryButton>
    </div>

    <div
      v-for="group in CONFLICT_FIELD_GROUPS"
      :key="group.key"
      class="flex flex-wrap gap-1.5 py-1"
    >
      <v-chip
        v-for="field in allowed(group.fields)"
        :key="field"
        size="small"
        label
        variant="outlined"
        :disabled="isDead(field)"
        :class="chipClasses(field)"
        :aria-pressed="String(hiddenFields.indexOf(field) === -1)"
        :data-cy="`conflict-field-chip-${field}`"
        @click="emit('toggle', field)"
      >
        {{ label(field) }}
        <span class="ml-1.5 tabular-nums normal-case opacity-70">
          {{ countLabel(field) }}
        </span>
      </v-chip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import {
  CONFLICT_FIELD_GROUPS,
  CONFLICT_GROUP_CLASSES,
  conflictFieldGroup,
  conflictFieldLabel,
} from '../constants/conflictFieldConstants';
import type { ConflictFieldId } from '../models/conflictTypes';

const props = defineProps<{
  fieldCounts: Partial<Record<ConflictFieldId, number>>;
  hiddenFields: ConflictFieldId[];
  hiddenRowCount: number;
  /** Fields the viewer may see at all — a gated field is absent, not shown empty. */
  permittedFields: ConflictFieldId[];
  /** False when the run did not cover time entries, so that pill reads as unsearched. */
  includedTimeEntries: boolean;
}>();

const emit = defineEmits<{ toggle: [field: ConflictFieldId]; showAll: [] }>();

const anyHidden = computed(() => props.hiddenFields.length > 0);

const statusText = computed(() =>
  props.hiddenRowCount
    ? `${props.hiddenRowCount} result${
        props.hiddenRowCount === 1 ? '' : 's'
      } hidden`
    : 'Click a field to hide its results',
);

const label = (field: ConflictFieldId) => conflictFieldLabel(field);

const allowed = (fields: ConflictFieldId[]) =>
  fields.filter((field) => props.permittedFields.indexOf(field) !== -1);

function isUnsearched(field: ConflictFieldId): boolean {
  return field === 'timeEntryDescription' && !props.includedTimeEntries;
}

/** No results and nothing to toggle — shown so the field still reads as searched. */
function isDead(field: ConflictFieldId): boolean {
  return isUnsearched(field) || !props.fieldCounts[field];
}

function countLabel(field: ConflictFieldId): string {
  if (isUnsearched(field)) return 'not searched';
  return String(props.fieldCounts[field] ?? 0);
}

function chipClasses(field: ConflictFieldId): string[] {
  if (props.hiddenFields.indexOf(field) !== -1)
    return ['line-through', 'opacity-60'];
  if (isDead(field)) return ['opacity-50'];
  return [CONFLICT_GROUP_CLASSES[conflictFieldGroup(field)]];
}
</script>
